import type {
  ApiReturnCampaign,
  ApiReturnSite,
  ApiReturnThematic,
} from '@highimpact-frontend/api-types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import ListDataGrid from '../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import { useCheckPermission } from '../../../components/utils/permission/helpers/usePermission';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import { useState } from 'react';
import campaignService from '../../../api/services/config/campaign/CampaignService';
import CreateCampaign from '../../../components/campaigns/CreateCampaign';
import EditCampaign from '../../../components/campaigns/EditCampaign';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import { getOrElse } from '../../../utils/functions';
import { useModal } from '../../../utils/hooks/useModal';

interface FilteredSitesProps {
  campaigns: ApiReturnCampaign[];
  projectId: number;
  sites: ApiReturnSite[];
  thematics: ApiReturnThematic[];
  refresh: () => void;
}

type RowMUI = Partial<Omit<ApiReturnCampaign, 'id'>> & {
  id: number;
  name?: string;
  start_date?: string;
  end_date?: string;
  thematic_ids?: number[] | null | undefined;
  site_ids?: number[] | null | undefined;
  tematic?: string;
  campaign?: ApiReturnCampaign;
};

const FilteredCampaigns: React.FC<FilteredSitesProps> = ({
  campaigns,
  projectId,
  sites,
  refresh,
  thematics,
}) => {
  const sortedCampaigns = campaigns.sort(
    (a, b) =>
      new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );
  const [filteredCampaigns] = useState<ApiReturnCampaign[]>(sortedCampaigns);

  const { submit } = useSubmit();

  const createModal = useModal();

  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    campaign_id: number;
  }>();

  const { shouldHide: hideEditButton, canDisable: disableEditButton } =
    useCheckPermission('configuration_automatic_device_edit_button');
  const { shouldHide: hideDeleteButton, canDisable: disableDeleteButton } =
    useCheckPermission('configuration_automatic_device_delete_button');

  const editModal = useModal<{ campaign: ApiReturnCampaign }>();

  const deleteCampaign = async (id: number) => {
    let response: unknown = null;
    submit({
      callbacks: [
        async () => (response = await campaignService.delete(id)),
        () => console.log('Success:', response),
        async () => refresh(),
      ],
      errorMessages: {
        anyError:
          'Une erreur est survenue lors de la suppression de la campagne.',
        unicityError: 'Vous avez déjà supprimé cette campagne',
      },
      successMessage: 'La campagne a été supprimée avec succès',
    });
  };

  const getThematicByCampaign = (campaign: ApiReturnCampaign) => {
    const thematic = thematics.find(
      (th) => th.id === (campaign.thematic_ids?.[0] ?? undefined)
    );
    if (thematic) {
      return thematic?.name;
    } else {
      return '--';
    }
  };

  const headingsPrettyNames: Record<string, string> = {
    name: 'Nom',
  };

  const createRow = (campaign: ApiReturnCampaign): RowMUI => {
    //const campaignElem = getEntityById(campaign.id, campaigns);
    return {
      id: campaign.id,
      name: getOrElse(campaign.name),
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      tematic: getOrElse(getThematicByCampaign(campaign)),
      campaign: campaign,
    };
  };

  const rows: RowMUI[] = filteredCampaigns.map(createRow);

  const createColumn = (field: keyof RowMUI): GridColDef<RowMUI> => ({
    field,
    headerName: headingsPrettyNames[field] ?? field,
    minWidth: 100,
    sortable: true,
    filterable: true,
    flex: 1,
    headerClassName: 'list-data-grid-style_table-header',
  });

  const columns: GridColDef<RowMUI>[] = [
    ...(Object.keys(headingsPrettyNames) as (keyof RowMUI)[]).map((key) =>
      createColumn(key)
    ),
    {
      field: 'start_date',
      headerName: 'Date de début',
      headerClassName: 'list-data-grid-style_table-header',
      flex: 1,
      minWidth: 100,
      sortable: true,
      filterable: true,
      valueGetter: (value) => {
        if (!value) {
          return '--';
        }
        const dateToFilter = new Date(value).getTime();
        return dateToFilter;
      },
      valueFormatter: (value) => {
        if (!value) {
          return '--';
        }
        const dateToShow = getOrElse(
          new Date(value).toLocaleDateString('en-UK').replace(/\//g, '-')
        );
        return dateToShow;
      },
    },

    {
      field: 'end_date',
      headerName: 'Date de fin',
      headerClassName: 'list-data-grid-style_table-header',
      flex: 1,
      minWidth: 100,
      sortable: true,
      filterable: true,
      valueGetter: (value) => {
        if (!value) {
          return '--';
        }
        const dateToFilter = new Date(value).getTime();
        return dateToFilter;
      },
      valueFormatter: (value) => {
        if (!value) {
          return '--';
        }
        const dateToShow = getOrElse(
          new Date(value).toLocaleDateString('en-UK').replace(/\//g, '-')
        );
        return dateToShow;
      },
    },

    {
      field: 'tematic',
      headerName: 'Thématique',
      minWidth: 100,
      sortable: true,
      filterable: true,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 100,
      headerClassName: 'list-data-grid-style_table-header',
      getActions: (
        params: import('@mui/x-data-grid-pro').GridRowParams<RowMUI>
      ) => {
        const row = params.row as RowMUI;
        return [
          !hideEditButton && (
            <GridActionsCellItem
              key={`update-${row.id}-${row.campaign?.id}`}
              icon={<EditIcon />}
              label="Modifier"
              onClick={() => {
                editModal.open({ campaign: params.row.campaign! });
              }}
              showInMenu
              disabled={disableEditButton}
            />
          ),

          !hideDeleteButton && (
            <GridActionsCellItem
              key={`delete-${row.id}-${row.campaign?.name}`}
              icon={<DeleteIcon />}
              label="Supprimer"
              onClick={() => {
                deleteModal.open({
                  callback: () => deleteCampaign(params.row.campaign!.id),
                  message: 'Voulez-vous supprimer la campagne',
                  target: params.row.campaign!.name,
                  campaign_id: params.row.campaign!.id,
                });
              }}
              showInMenu
              disabled={disableDeleteButton}
            />
          ),
        ].filter(Boolean) as React.ReactElement[];
      },
    },
  ];

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2
          item={'campaigns_create_button'}
          project_id={Number(projectId)}
          //site_id={Number(site_id)}
        >
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
          >
            Créer une campagne
          </button>
        </PermissionDecoratorV2>
      </div>

      {campaigns.length !== 0 ? (
        <ListDataGrid
          rows={rows}
          columns={columns}
          checkboxSelection={false}
          height="calc(100vh - 80px - 125px)"
          /*Props that allows define customizations for header row (all cells in header*/
          slotProps={{
            columnMenu: {
              slots: {
                columnMenuSortItem: null,
                columnMenuColumnsItem: null,
                columnMenuPinningItem: null,
              },
            },
          }}
        />
      ) : (
        <div className="uk-alert-warning uk-margin uk-padding-small" uk-alert>
          Pas de campagnes pour ce site
        </div>
      )}

      {
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback()}
          open={deleteModal.isOpen}
          title={'Confirmation'}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      }

      {campaigns && (
        <CreateCampaign
          sites={sites}
          thematics={thematics}
          onCancel={createModal.close}
          isOpen={createModal.isOpen}
          refresh={refresh}
        />
      )}

      {editModal.params?.campaign && (
        <EditCampaign
          sites={sites}
          thematics={thematics}
          onCancel={editModal.close}
          isOpen={editModal.isOpen}
          campaign={editModal.params?.campaign}
          refresh={refresh}
        />
      )}
    </div>
  );
};

export default FilteredCampaigns;
